import * as React from 'react'
import { useRouterState } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useT } from '@/i18n'
import { matchRoute } from '@/help/resolve'
import { isEditableTarget } from '@/lib/utils'
import { endTour, tourStore, useActiveTour, useSaveTour, type ActiveTour } from './state'
import { tourById } from './tours'
import type { Tour } from './types'

/** Is the element rendered with a box (not display:none, not zero-sized)? */
export function isShown(el: Element): boolean {
  const r = el.getBoundingClientRect()
  if (r.width <= 0 && r.height <= 0) return false
  const cs = window.getComputedStyle(el)
  return cs.visibility !== 'hidden' && cs.display !== 'none'
}

/** The first visible element for a step anchor (one id or several alternatives). */
export function findAnchor(anchor: string | string[] | undefined): HTMLElement | null {
  for (const id of [anchor ?? []].flat()) {
    for (const el of document.querySelectorAll<HTMLElement>(`[data-tour="${id}"]`)) if (isShown(el)) return el
  }
  return null
}

const GAP = 16
const EDGE = 16

/**
 * Where to put the step card: below the anchor, else above, else beside it, always
 * clamped inside the viewport. No anchor: centred.
 */
export function placeCard(rect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'> | null, h: number, vw: number, vh: number): React.CSSProperties {
  const width = Math.min(360, vw - 2 * EDGE)
  if (!rect) return { top: '50%', left: '50%', width, transform: 'translate(-50%, -50%)' }
  const clampTop = (t: number) => Math.min(Math.max(EDGE, t), Math.max(EDGE, vh - h - EDGE))
  const clampLeft = (l: number) => Math.min(Math.max(EDGE, l), Math.max(EDGE, vw - width - EDGE))
  if (rect.bottom + GAP + h <= vh - EDGE) return { top: rect.bottom + GAP, left: clampLeft(rect.left), width }
  if (rect.top - GAP - h >= EDGE) return { top: rect.top - GAP - h, left: clampLeft(rect.left), width }
  if (rect.right + GAP + width <= vw - EDGE) return { top: clampTop(rect.top), left: rect.right + GAP, width }
  if (rect.left - GAP - width >= EDGE) return { top: clampTop(rect.top), left: rect.left - GAP - width, width }
  return { top: clampTop(vh - h - EDGE), left: clampLeft(rect.left), width }
}

/** How long to wait for an anchor to render (lazy pages, queries) before skipping the step. */
export const ANCHOR_WAIT_MS = 1500
const POLL_MS = 100

/**
 * Renders the active product tour: a spotlight around the step's anchor and a
 * non-modal step card. The page stays usable underneath; missing anchors are skipped.
 */
export function TourRunner() {
  const active = useActiveTour()
  const tour = tourById(active?.id)
  if (!active || !tour) return null
  return <RunningTour key={tour.id} tour={tour} active={active} />
}

function RunningTour({ tour, active }: { tour: Tour; active: ActiveTour }) {
  const t = useT()
  const save = useSaveTour()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const step = tour.steps[Math.min(active.step, tour.steps.length - 1)]
  const [target, setTarget] = React.useState<HTMLElement | null>(null)
  const [ready, setReady] = React.useState(false)
  const [rect, setRect] = React.useState<DOMRect | null>(null)
  const cardRef = React.useRef<HTMLDivElement>(null)
  const [cardH, setCardH] = React.useState(220)
  const returnFocus = React.useRef<Element | null>(null)
  const titleId = React.useId()

  React.useEffect(() => {
    returnFocus.current = document.activeElement
    return () => {
      const el = returnFocus.current as HTMLElement | null
      if (el && el.isConnected && typeof el.focus === 'function') el.focus()
    }
  }, [])

  const go = React.useCallback(
    (step: number, dir: 1 | -1) => {
      if (step >= tour.steps.length) {
        void save(tour.id, 'completed', tour.steps.length - 1)
        endTour()
        toast.success(t('tour.completed'))
        return
      }
      const next = Math.max(0, step)
      tourStore.set({ active: { id: tour.id, step: next, dir: step < 0 ? 1 : dir } })
      void save(tour.id, 'in_progress', next)
    },
    [tour, save, t],
  )
  const pause = React.useCallback(() => {
    void save(tour.id, 'in_progress', active.step)
    endTour()
    toast(t('tour.paused'))
  }, [tour.id, active.step, save, t])
  const skip = React.useCallback(() => {
    void save(tour.id, 'skipped', active.step)
    endTour()
  }, [tour.id, active.step, save])

  // Leaving the tour's page pauses it (it can be resumed from the help panel).
  const offRoute = !!tour.route && !matchRoute(tour.route, pathname)
  React.useEffect(() => {
    if (offRoute) pause()
  }, [offRoute, pause])

  // Find the anchor, waiting briefly for it to render; skip the step when it never shows.
  React.useEffect(() => {
    setReady(false)
    setTarget(null)
    if (!step.anchor) {
      setReady(true)
      return
    }
    let waited = 0
    let timer: ReturnType<typeof setTimeout>
    const look = () => {
      const el = findAnchor(step.anchor)
      if (el) {
        el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
        setTarget(el)
        setReady(true)
      } else if (waited >= ANCHOR_WAIT_MS) {
        go(active.step + active.dir, active.dir)
      } else {
        waited += POLL_MS
        timer = setTimeout(look, POLL_MS)
      }
    }
    look()
    return () => clearTimeout(timer)
  }, [step, active.step, active.dir, go])

  // Follow the anchor on scroll / resize.
  React.useLayoutEffect(() => {
    if (!target) {
      setRect(null)
      return
    }
    const update = () => setRect(target.getBoundingClientRect())
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [target])

  // Measure the card so it can be placed fully inside the viewport.
  React.useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight
    if (h && Math.abs(h - cardH) > 2) setCardH(h)
  }, [cardH, active.step, ready, rect])

  // Move focus into the card on every step so keyboard and screen-reader users follow along.
  React.useEffect(() => {
    if (ready) cardRef.current?.querySelector<HTMLElement>('[data-tour-primary]')?.focus()
  }, [ready, active.step])

  // Keyboard: Esc pauses, ← / → move.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        pause()
      } else if (!isEditableTarget(e.target) && e.key === 'ArrowRight') {
        e.preventDefault()
        go(active.step + 1, 1)
      } else if (!isEditableTarget(e.target) && e.key === 'ArrowLeft' && active.step > 0) {
        e.preventDefault()
        go(active.step - 1, -1)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [active.step, go, pause])

  // Wait for the anchor's measured box, so the spotlight never animates in from elsewhere.
  if (!ready || offRoute || (target && !rect)) return null
  const last = active.step >= tour.steps.length - 1
  const pad = 6
  const cardStyle = placeCard(rect, cardH, window.innerWidth, window.innerHeight)
  return (
    <div className="pointer-events-none fixed inset-0 z-[60]" data-testid="tour">
      {rect ? (
        <div
          key="spot"
          aria-hidden
          className="absolute rounded-[10px] ring-2 ring-brand transition-[top,left,width,height] duration-200 motion-reduce:transition-none"
          style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.45)' }}
        />
      ) : (
        <div key="dim" aria-hidden className="absolute inset-0 bg-black/45" />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-describedby={`${titleId}-body`}
        data-tour-card
        className="pointer-events-auto absolute flex flex-col gap-3 rounded-[12px] border border-border bg-surface p-4 text-ink shadow-pop"
        style={cardStyle}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11.5px] font-medium uppercase tracking-wide text-muted">{t('tour.label', { title: tour.title })}</div>
            <h2 id={titleId} className="mt-0.5 text-[15px] font-semibold text-ink-strong">
              {step.title}
            </h2>
          </div>
          <Button variant="ghost" size="icon-sm" aria-label={t('tour.pause')} onClick={pause}>
            <X />
          </Button>
        </div>
        <p id={`${titleId}-body`} className="text-[13.5px] leading-relaxed">
          {step.body}
        </p>
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-[12px] text-muted tabular" aria-live="polite">
            {t('tour.step', { n: active.step + 1, total: tour.steps.length })}
          </span>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={skip}>
            {t('tour.skip')}
          </Button>
          {active.step > 0 && (
            <Button variant="outline" size="sm" onClick={() => go(active.step - 1, -1)}>
              {t('tour.back')}
            </Button>
          )}
          <Button size="sm" data-tour-primary onClick={() => go(active.step + 1, 1)}>
            {last ? t('tour.done') : t('tour.next')}
          </Button>
        </div>
      </div>
    </div>
  )
}
